import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  type User,
} from 'firebase/auth'
import { doc, getDoc, updateDoc } from 'firebase/firestore'
import { setAuthPlaceId } from '../lib/authPlaceStore'
import { authProfileLoadErrorMessage, getDocWithBootTimeout } from '../lib/authProfileLoad'
import { clearPlaceStorage } from '../lib/placeStorage'
import { ensureAuthPersistence, getFirebaseAuth } from '../lib/firebase'
import { getFirestoreInstance, useFirestore } from '../lib/firebase'
import { clearAllCaches } from '../lib/firestore'
import { bumpAuthTrustEpochIfNeeded, getAuthTrustEpochMs } from '../lib/peripheralTrustLocal'
import { setAuditActor } from '../lib/auditActorContext'
import { getOrCreateInstallId } from '../lib/deviceStorage'
import { issuePosResume, revokePosResume } from '../lib/posResumeApi'
import { clearPosResumeAccount, readPosResumeAccount, writePosResumeAccount } from '../lib/posResumeLocal'

export interface UserProfile {
  defaultPlaceId: string
  companyId: string
  name: string
  email: string
  position: string
}

export interface RegisterInput {
  name: string
  email: string
  position: string
  companyName: string
  placeName: string
  address: string
  password: string
}

interface AuthContextValue {
  user: User | null
  userProfile: UserProfile | null
  loading: boolean
  /** False until the first onAuthStateChanged callback (session restore on cold start). */
  authReady: boolean
  /** True when session restore is still pending after 30 seconds. The sign-in form stays hidden. */
  authRestoreSlow: boolean
  /** Set when Firebase Auth config is missing or invalid (e.g. auth/configuration-not-found). */
  authError: string | null
  /**
   * Set when Auth has a user but Firestore profile load failed or the users/{uid} doc is missing.
   * Null when profile loaded successfully or there is no signed-in user.
   */
  profileError: string | null
  /** Local trust floor (ms) when auth user appears or uid changes; drives System printer re-verify. */
  authTrustEpochMs: number
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  register: (input: RegisterInput) => Promise<string>
  /** Saves the place this account opens next, and updates the in-memory profile. */
  rememberDefaultPlace: (placeId: string) => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth(): AuthContextValue | null {
  return useContext(AuthContext)
}

const CONFIG_NOT_FOUND = 'auth/configuration-not-found'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [authReady, setAuthReady] = useState(false)
  const [authRestoreSlow, setAuthRestoreSlow] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)
  const [profileError, setProfileError] = useState<string | null>(null)
  const [authTrustEpochMs, setAuthTrustEpochMs] = useState(() =>
    typeof window !== 'undefined' ? getAuthTrustEpochMs() : 0,
  )
  const authUidRef = useRef<string | null>(null)
  const resumeEpochRef = useRef(0)

  const auth = getFirebaseAuth()

  useEffect(() => {
    if (!auth) {
      setLoading(false)
      setAuthReady(true)
      setAuthRestoreSlow(false)
      return
    }
    setAuthError(null)
    setAuthRestoreSlow(false)
    let unsubscribe: (() => void) | undefined
    let sawAuthEvent = false
    void ensureAuthPersistence().catch(() => {})
    const restoreSlowTimeout = window.setTimeout(() => {
      if (sawAuthEvent) return
      setAuthRestoreSlow(true)
    }, 30_000)
    try {
      unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        sawAuthEvent = true
        setAuthRestoreSlow(false)
        const nextUid = firebaseUser?.uid ?? null
        const prevUid = authUidRef.current
        authUidRef.current = nextUid
        setAuthTrustEpochMs(bumpAuthTrustEpochIfNeeded(prevUid, nextUid))

        setUser(firebaseUser)
        if (!firebaseUser) {
          setUserProfile(null)
          setProfileError(null)
          setAuthPlaceId(null)
          clearPlaceStorage()
          clearAllCaches()
          setLoading(false)
          setAuthReady(true)
          return
        }

        setLoading(true)
        setProfileError(null)
        try {
          if (useFirestore()) {
            try {
              const db = getFirestoreInstance()
              const profileSnap = await getDocWithBootTimeout(
                getDoc(doc(db, 'users', firebaseUser.uid)),
                'account profile',
              )
              if (profileSnap.exists()) {
                const data = profileSnap.data() as UserProfile
                if (!data.defaultPlaceId?.trim()) {
                  setUserProfile(data)
                  setAuthPlaceId(null)
                  setProfileError(
                    'Your account profile is missing a default place. Sign out and register again, or ask an admin to fix users/' +
                      firebaseUser.uid +
                      '.',
                  )
                } else {
                  setUserProfile(data)
                  setAuthPlaceId(data.defaultPlaceId)
                  setProfileError(null)
                }
              } else {
                setUserProfile(null)
                setAuthPlaceId(null)
                setProfileError(
                  'No account profile found in Firestore for this login. Sign out and complete registration, or use the account that was registered for this preview project.',
                )
              }
            } catch (err) {
              console.error('[auth] failed to load users/' + firebaseUser.uid, err)
              setUserProfile(null)
              setAuthPlaceId(null)
              setProfileError(authProfileLoadErrorMessage(err))
            }
          } else {
            setUserProfile(null)
            setAuthPlaceId(null)
            setProfileError(
              'This build has Firestore disabled (VITE_USE_FIRESTORE is not true). Redeploy preview with Firebase env set.',
            )
          }
        } finally {
          setLoading(false)
          setAuthReady(true)
        }
      })
    } catch (err: unknown) {
      sawAuthEvent = true
      const code = err && typeof err === 'object' && 'code' in err ? (err as { code: string }).code : ''
      if (code === CONFIG_NOT_FOUND) {
        setAuthError('Firebase Auth is not configured. Check your .env (VITE_FIREBASE_*) and enable Authentication in Firebase Console.')
      }
      setAuthRestoreSlow(false)
      setLoading(false)
      setAuthReady(true)
    }
    return () => {
      window.clearTimeout(restoreSlowTimeout)
      unsubscribe?.()
    }
  }, [auth])

  useEffect(() => {
    if (!user) {
      setAuditActor(null)
      return
    }
    const name =
      userProfile?.name?.trim() ||
      user.displayName?.trim() ||
      user.email?.trim() ||
      'Staff'
    setAuditActor({ uid: user.uid, name })
  }, [user, userProfile])

  useEffect(() => {
    if (!user || user.isAnonymous) return
    const existing = readPosResumeAccount()
    if (existing && existing.uid === user.uid) return
    const epoch = resumeEpochRef.current
    const uid = user.uid
    const issuedAt = Date.now()
    let active = true
    void issuePosResume(getOrCreateInstallId(), issuedAt)
      .then((issued) => {
        if (!active || epoch !== resumeEpochRef.current) return
        if (!issued.token || issued.uid !== uid) return
        writePosResumeAccount(issued)
      })
      .catch(() => {})
    return () => {
      active = false
    }
  }, [user])

  const login = useCallback(
    async (email: string, password: string) => {
      if (!auth) throw new Error('Firebase Auth is not configured')
      await signInWithEmailAndPassword(auth, email, password)
    },
    [auth],
  )

  const logout = useCallback(async () => {
    if (!auth) return
    resumeEpochRef.current += 1
    try {
      await revokePosResume(getOrCreateInstallId())
    } catch {
      // The local account is cleared either way. A failed revoke expires on the server.
    }
    clearPosResumeAccount()
    await signOut(auth)
    setUserProfile(null)
    setAuthPlaceId(null)
    clearPlaceStorage()
    clearAllCaches()
  }, [auth])

  // Maintenance release: legacy client-side business provisioning is retired.
  const register = useCallback(async (_input: RegisterInput): Promise<string> => {
    throw new Error('Self-service signup is temporarily closed.')
  }, [])

  const rememberDefaultPlace = useCallback(
    async (placeId: string) => {
      const id = placeId.trim()
      if (!id || !user) return
      if (useFirestore()) {
        await updateDoc(doc(getFirestoreInstance(), 'users', user.uid), { defaultPlaceId: id })
      }
      setUserProfile((prev) => (prev ? { ...prev, defaultPlaceId: id } : prev))
      setAuthPlaceId(id)
    },
    [user],
  )

  const value: AuthContextValue = {
    user,
    userProfile,
    loading,
    authReady,
    authRestoreSlow,
    authError,
    profileError,
    authTrustEpochMs,
    login,
    logout,
    register,
    rememberDefaultPlace,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
